import React, { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useCombinedStore } from "../../store";
import * as AlfredoStyles from "../../styles/components/configuration/index";
import { useAbasAcessiveis } from "../../hooks/useAbasAcessiveis";
import * as AlfredoInterfaces from "../../interfaces";
import * as AlfredoComponents from "..";
import { useConfiguration } from "../../hooks/useConfiguration";
import { useModal } from "../../hooks/useModal";
import { ConfigurationProps } from "./types";
import api from "../../services/api.service";
import itemsService from "../../services/item.service";
import { atualizarAmbiente, criarAmbiente } from "../../services/ambient.service";

// Import sections
// A rede de proteção que faltava. Até este card, uma exceção em QUALQUER
// sub-tela desta área desmontava a árvore inteira do React e a página ficava
// branca, sem aviso — foi assim que "Avançado > Logs" ficou invisível por
// meses. Enquanto nada lança, o boundary devolve `children` puro: nenhum
// elemento a mais, nenhum pixel a mais nas telas que já funcionavam.
import ErrorBoundary from "../ErrorBoundary";
import AccountSection from "./sections/accountSection";
import StoresSection from "./sections/storesSection";
import SuppliesSection from "./sections/suppliesSection";
import EmployeesSection from "./sections/employeesSection";
import AdvancedSection from "./sections/advancedSection";
import CardapioSection from "./sections/cardapioSection";
import DescontosSection from "./sections/descontosSection";
import ComunicacoesSection from "./sections/comunicacoesSection";

// Import modals
import CompanyModal from "./modals/CompanyModal";
import SubsidiaryModal from "./modals/SubsidiaryModal";
import ItemModal from "./modals/ItemModal";
import PeriodTypeModal from "./modals/PeriodTypeModal";
import CreateSKUModal from "./modals/CreateSKUModal";
import UpdateSKUModal from "./modals/UpdateSKUModal";
// Os nove que eram chamados e não existiam.
import AmbientModal from "./modals/AmbientModal";
import SupplierModal from "./modals/SupplierModal";
import LimitModal from "./modals/LimitModal";
import MovementTypeModal from "./modals/MovementTypeModal";
import BOMModal from "./modals/BOMModal";
import CategoryModal from "./modals/CategoryModal";
import GroupModal from "./modals/GroupModal";
import LocalDeEstoqueModal from "./modals/LocalDeEstoqueModal";
import AlertConfigModal from "./modals/AlertConfigModal";
import MessageTemplateModal from "./modals/MessageTemplateModal";

/**
 * As colunas que `acd."ActCompany"` e `acd."ActSubsidiary"` REALMENTE têm, e os
 * campos que os dois formulários coletam e não têm onde ser gravados.
 *
 * Levantado do `schema.prisma`, coluna a coluna, não da memória. Mandar um
 * campo inexistente ao `PATCH /company/:uuid` não é inofensivo: o controller
 * repassa o body direto para `prisma.actCompany.update({ data })`, e o Prisma
 * responde "Unknown argument" — um campo fantasma derruba o salvamento inteiro,
 * inclusive os campos legítimos que iam na mesma requisição.
 *
 * A lista de fora não é decoração: é o que a tela precisa DIZER ao usuário, em
 * vez de aceitar a digitação e jogar fora em silêncio. Dar casa a esses campos
 * é §18 da spec (modelo de dados que precisa nascer) e não cabe neste card.
 */
const COLUNAS_DE_EMPRESA = [
  "companyName",
  "companyLogoUrl",
  "companyAddress",
  "companyTaxPayerID",
] as const;

const CAMPOS_SEM_COLUNA_EMPRESA = [
  "companyEmail",
  "companyPhone",
  "companyCity",
  "companyState",
  "companyDescription",
] as const;

const COLUNAS_DE_LOJA = [
  "subsidiaryName",
  "subsidiaryLogoUrl",
  "subsidiaryAddress",
  "subsidiaryTaxPayerID",
  "subsidiaryVirtualAddress",
] as const;

const CAMPOS_SEM_COLUNA_LOJA = [
  "subsidiaryEmail",
  "subsidiaryPhone",
  "subsidiaryCity",
  "subsidiaryState",
  "subsidiaryDescription",
  "subsidiaryNotes",
] as const;

/**
 * De qual modal para qual agregado do REST de suprimentos.
 *
 * Existe uma tabela em vez de catorze `case` porque criar e editar são o MESMO
 * destino: o que muda é ter ou não o uuid. `chave` é o nome da coluna de
 * identidade — é por ela que se decide entre POST e PATCH, e nunca por
 * `selectedObject` sozinho, que é `{}` (truthy) na criação.
 */
const AGREGADO_DO_MODAL: Record<
  string,
  { rota: string; chave: string; rotulo: string; exigeNome: boolean }
> = {
  createSupplier: { rota: "fornecedores", chave: "fornecedorUuid", rotulo: "Fornecedor", exigeNome: true },
  updateSupplier: { rota: "fornecedores", chave: "fornecedorUuid", rotulo: "Fornecedor", exigeNome: true },
  createLimit: { rota: "limites-de-reposicao", chave: "limiteUuid", rotulo: "Limite de reposição", exigeNome: false },
  updateLimit: { rota: "limites-de-reposicao", chave: "limiteUuid", rotulo: "Limite de reposição", exigeNome: false },
  createMovementType: { rota: "tipos-de-movimento", chave: "tipoDeMovimentoUuid", rotulo: "Tipo de movimento", exigeNome: true },
  updateMovementType: { rota: "tipos-de-movimento", chave: "tipoDeMovimentoUuid", rotulo: "Tipo de movimento", exigeNome: true },
  createBOM: { rota: "fichas-tecnicas", chave: "fichaUuid", rotulo: "Ficha técnica", exigeNome: false },
  updateBOM: { rota: "fichas-tecnicas", chave: "fichaUuid", rotulo: "Ficha técnica", exigeNome: false },
  createCategory: { rota: "categorias", chave: "categoriaUuid", rotulo: "Categoria", exigeNome: true },
  updateCategory: { rota: "categorias", chave: "categoriaUuid", rotulo: "Categoria", exigeNome: true },
  createGroup: { rota: "grupos", chave: "grupoUuid", rotulo: "Grupo", exigeNome: true },
  updateGroup: { rota: "grupos", chave: "grupoUuid", rotulo: "Grupo", exigeNome: true },
  createLocalEstoque: { rota: "locais-de-estoque", chave: "localUuid", rotulo: "Local de estoque", exigeNome: true },
  updateLocalEstoque: { rota: "locais-de-estoque", chave: "localUuid", rotulo: "Local de estoque", exigeNome: true },
};

/**
 * O que dizer quando o formulário está pronto e o endpoint não.
 *
 * Cada texto NOMEIA a rota que falta. É a diferença entre uma informação e um
 * modal que some — e é o que o card exige em vez de fingir persistência.
 */
const RECUSA_SEM_ROTA: Record<string, string> = {
  alertConfig:
    'Configuração de alerta ainda não tem rota: falta o endpoint de ActSubsidiaryInfo tipo 23 ("Alerta da unidade"). O formulário está pronto; nada foi enviado.',
  messageTemplate:
    "Template de mensagem ainda não tem rota: falta o endpoint de ActOrganizationInfo tipo 20. O texto que você escreveu não foi gravado.",
  canalComunicacao:
    "Canal de comunicação ainda não tem rota: falta o endpoint de ActSubsidiaryInfo tipo 24. Nada foi enviado — e nenhuma mensagem foi disparada.",
  automacao:
    "Automação ainda não tem rota: falta o endpoint de ActOrganizationInfo tipo 21. Nada foi enviado.",
  desconto:
    'Desconto ainda não tem rota: acd."ActPeriodTypeInfo" tipo 1 existe no banco, mas nenhum dos 10 routers do backend a expõe. Nada foi gravado.',
  taxa:
    'Taxa ainda não tem rota: acd."ActPeriodTypeInfo" tipo 2 existe no banco, mas nenhum dos 10 routers do backend a expõe. Nada foi gravado.',
  // Colaboradores. O vínculo usuário × loja × papel e o campo "atendente"
  // cabem em ActUserGroup/ActUserGroupInfo, que já existem — falta a rota. A
  // ALÇADA (quem aprova o quê, até que valor) não cabe: exige as entidades
  // Alcada e Aprovacao, que este card não criou. Formulário de alçada sem
  // tabela seria formulário morto, e o card proíbe isso por escrito.
  employee:
    "Vínculo de colaborador (usuário × loja × papel) ainda não tem rota: ActUserGroupInfo existe no banco e nenhum router a expõe. Nada foi gravado.",
  access:
    "Alçada e permissão por usuário dependem das entidades Alcada e Aprovacao (§18), que ainda não existem no banco. Nada foi gravado — e nenhum limite de aprovação passou a valer.",
};

const Configuration: React.FC<ConfigurationProps> = ({
  decryptedInfoToken,
  decryptedToken,
}) => {
  const { setOrganizationZustand, setMessageZustand } = useCombinedStore();
  const formRefs = useRef<any>({});

  /** A rede do usuário. Todo agregado de suprimentos é escopado por ela. */
  const organizacaoDoUsuario =
    decryptedToken?.userInfo?.userGroupInfo?.[0]?.organization;

  const [parametrosDaUrl, definirParametrosDaUrl] = useSearchParams();

  const {
    formData,
    isEditing,
    tempFormData,
    selectedInfo,
    selectedHorizontalOption,
    selectedVerticalOption,
    expandedAccordion,
    filter,
    uploadedImage,
    productCategoryTableVisibleColumns,
    productGroupTableVisibleColumns,
    updateState,
    toggleAccordion,
  } = useConfiguration();

  const {
    isModalOpen,
    modalType,
    selectedObject,
    handleOpenModal,
    handleCloseModal,
  } = useModal();

  // ATENÇÃO — aba nova exige TRÊS edições casadas, por string, sem type-check
  // ligando as três: este array, a chave de `verticalOptions` e o `case` de
  // `renderSessionContent`. Divergir dá tela em branco silenciosa.
  const horizontalOptions = [
    "Minha Conta",
    "Lojas",
    "Cardápio",
    "Suprimentos",
    "Descontos e Taxas",
    "Comunicações",
    "Colaboradores",
    "Avançado",
  ];

  const verticalOptions: { [key: string]: string[] } = {
    "Minha Conta": [
      "Dados Pessoais",
      "Segurança e Privacidade",
      "Preferências",
      "Ajuda e Suporte",
      "Enviar Feedback",
    ],
    Lojas: [
      "Empresa",
      "Estabelecimentos",
      "Lojas",
      // O site é da MARCA (company) e da REDE (organization) — a unidade não tem
      // página, ela é destino de pedido dentro da landing. Fica em Lojas porque
      // quem edita o site do restaurante é quem administra o restaurante.
      "Site",
      "Meios de Pagamento",
      "Ambientes",
      "Itens",
      // Períodos: o PeriodTypeModal (7 seções) já existia e nenhuma entrada do
      // sistema o abria. Layout de Impressão: a tela existe desde sempre em
      // /settings/layout-impressao e `grep -rn "layout-impressao" src` só
      // encontrava o próprio arquivo de rotas — só chegava lá quem digitasse a
      // URL.
      "Períodos",
      "Layout de Impressão",
    ],
    Suprimentos: [
      "SKU",
      // Categorias (tableType 2) e Grupos (tableType 3) tinham as colunas
      // desenhadas e NENHUMA entrada de menu.
      "Categorias",
      "Grupos",
      "Fornecedores",
      "Limites de Reposição",
      "Tipos de Movimentos",
      // As duas telas que estavam construídas e inalcançáveis: o switch de
      // suppliesSection atendia 6 opções e o menu listava 4. "Alertas e
      // Notificações" tem 874 linhas e ninguém nunca chegou nela.
      "Lista de Materiais",
      "Alertas e Notificações",
      // Locais de Estoque nasce com a Fase 4 (tem tabela própria). Antes disso
      // seria formulário sem onde gravar.
      "Locais de Estoque",
    ],
    // "Horários" e "Espelho de Ponto" SAEM (§9.5/§20.4): as duas nunca tiveram
    // case no employeesSection, nem entidade no schema, nem rota. Menu que leva
    // a "Sessão não encontrada" é pior do que menu sem a entrada.
    Colaboradores: ["Equipe", "Acessos"],
    Avançado: ["Integrações", "Permissões", "Logs", "Plano e Cobrança"],
    "Descontos e Taxas": ["Descontos", "Taxas"],
    Comunicações: ["Canais", "Templates", "Automações", "Log de envio"],
    Cardápio: [
      "Visão geral",
      "Produtos",
      "Combos",
      "Preço por canal",
      "Saneamento",
    ],
  };

  // ------------------------------------------------------------------
  // A ABA VIRA URL.
  //
  // Antes, toda tela desta área era inalcançável por link: a Configuração
  // guardava a aba num useState e /settings sempre abria em "Minha Conta >
  // Dados Pessoais". Ninguém conseguia mandar "olha esta tela" para outra
  // pessoa, nem tirar um print reproduzível, nem a LeftBar podia navegar para
  // uma aba específica — que é justamente o que faltava para o item de menu
  // "Configuração" deixar de ser decorativo.
  //
  // A leitura VALIDA contra as listas: `?aba=Inventado` cairia num
  // `verticalOptions[undefined]` e a tela ficaria em branco. Aba desconhecida é
  // ignorada em silêncio, e o padrão de sempre prevalece.
  useEffect(() => {
    const abaDaUrl = parametrosDaUrl.get("aba");
    const subDaUrl = parametrosDaUrl.get("sub");
    if (!abaDaUrl || !horizontalOptions.includes(abaDaUrl)) return;
    if (
      abaDaUrl === selectedHorizontalOption &&
      (!subDaUrl || subDaUrl === selectedVerticalOption)
    ) {
      return;
    }
    const subValida =
      subDaUrl && verticalOptions[abaDaUrl]?.includes(subDaUrl)
        ? subDaUrl
        : verticalOptions[abaDaUrl][0];
    updateState({
      selectedHorizontalOption: abaDaUrl,
      selectedVerticalOption: subValida,
    });
    // Só reage a mudança de URL. Incluir o estado aqui criaria um laço com o
    // efeito de escrita abaixo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametrosDaUrl]);

  /**
   * Escreve a aba na URL. Chamada pelo CLIQUE, nunca por efeito.
   *
   * A primeira versão disto era um segundo `useEffect`, e ele BRIGAVA com o de
   * cima: ao abrir `/settings?aba=Suprimentos`, o efeito de hidratação pedia
   * `updateState(Suprimentos)` e o de escrita rodava no MESMO commit, ainda
   * enxergando o estado velho ("Minha Conta"), e reescrevia a URL de volta para
   * o valor velho. Os dois ficavam se corrigindo, cada `navigate` remontava a
   * árvore, e a tabela de Suprimentos disparava NOVE requisições e ficava vazia
   * — a resposta de cada montagem era descartada pelo cleanup da anterior.
   *
   * O clique é a única origem legítima de troca de aba pelo usuário. Com a
   * escrita presa a ele, não há laço: a URL manda no estado (efeito de cima), e
   * o clique manda na URL.
   *
   * `replace` de propósito: trocar de aba não é navegação, é troca de vista.
   * Com push, o botão Voltar passaria a desfazer cliques de aba um a um em vez
   * de sair da Configuração.
   */
  const irParaAba = (aba: string, sub: string) => {
    updateState({
      selectedHorizontalOption: aba,
      selectedVerticalOption: sub,
    });
    definirParametrosDaUrl({ aba, sub }, { replace: true });
  };

  // Função para criar uma ref de forma dinâmica
  const setRef = (name: string) => (element: any) => {
    if (name.includes("organizationInfoValue")) {
      const subField = name.split(".")[1];
      formRefs.current.organizationInfoValue = {
        ...formRefs.current.organizationInfoValue,
        [subField]: element,
      };
    } else {
      formRefs.current[name] = element;
    }
  };

  const parseInfoValue = (infoValue: string) => {
    try {
      return JSON.parse(infoValue);
    } catch (error) {
      console.error("Invalid JSON string");
      return {};
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    if (field === "logoSize") {
      updateState({
        formData: {
          ...formData,
          logoWidht: e.target.value,
          logoHeight: e.target.value,
        },
      });
    } else {
      updateState({
        formData: {
          ...formData,
          [field]: e.target.value,
        },
      });
    }
  };

  const handleSave = async (objectType: any, payload?: any) => {
    // Nenhum modal fecha quando o salvamento não deu certo. Fechar igual ao
    // sucesso é a mentira original: o usuário vê o modal sumir e vai embora
    // achando que gravou.
    //
    // Isto começou valendo só para o `case "item"`; agora vale para todos, que
    // é o que torna qualquer coisa desta tela verificável. Some com o
    // `default: break` silencioso de brinde: um objectType sem case cai na
    // recusa nomeada lá embaixo, com o modal aberto.
    let manterModalAberto = false;

    // O que dizer ao usuário, DEVOLVIDO para quem chamou.
    //
    // `setMessageZustand` sozinho não serve: o `MessageBox` que pinta essa
    // mensagem só está montado em LoginPage e RegisterPage. Dentro da
    // Configuração ela é gravada na store e não aparece em lugar nenhum — foi
    // assim que o "N tela(s) salva(s)" existiu no código sem nunca ter sido
    // lido por ninguém. Quem mostra é o próprio modal, no molde do aviso da aba
    // Meios de Pagamento.
    let resultado: {
      ok: boolean;
      tom: "ok" | "erro" | "atencao";
      texto: string;
    } | null = null;
    const avisar = (
      tom: "ok" | "erro" | "atencao",
      texto: string,
      tipoDaStore: string
    ) => {
      resultado = { ok: tom === "ok", tom, texto };
      setMessageZustand(texto, tipoDaStore);
      // Aviso que o usuário não chega a ler é aviso que não existe: enquanto o
      // salvamento não deu certo, o modal fica aberto com o recado.
      if (tom !== "ok") manterModalAberto = true;
    };

    try {
      let response;

      switch (objectType) {
        case "itemInfo":
          const updatedItemInfo = {
            ...selectedInfo,
            updatedBy: decryptedToken.userInfo.userUuid,
            updatedAt: new Date().toISOString(),
            itemInfoValue: JSON.stringify(tempFormData),
            itemInfoType: selectedInfo.itemInfoType,
            itemInfoName: selectedInfo.itemInfoName,
            itemInfoDescription: selectedInfo.itemInfoDescription,
            integrated: selectedInfo.integrated,
            itemInfoStatus: selectedInfo.itemInfoStatus,
            createdAt: selectedInfo.createdAt,
            item: selectedInfo.item,
          };

          response = await api.patch(
            `itemInfo/${updatedItemInfo.itemInfoUuid}`,
            updatedItemInfo
          );

          // A rota passou a devolver { infoToken, fila } em vez do token cru,
          // para dizer se a alteração desce agora ou só na abertura do caixa.
          setOrganizationZustand({
            InfoToken: response.data?.infoToken ?? response.data,
          });
          if (response.data?.fila?.mensagem) {
            setMessageZustand(response.data.fila.mensagem, "success");
          }
          break;
        case "createSKU": {
          // O corpo sai do PAYLOAD que o modal manda, não de `formRefs`.
          // `CreateSKUModal` virou estado local e não popula ref nenhuma: ler
          // `formRefs.current` aqui fazia o POST sair com
          // `organizationInfoName: undefined` e `organizationInfoValue:
          // JSON.stringify(undefined)` — ou seja, cadastro vazio devolvendo
          // 2xx, que é a pior forma de falhar.
          const nome = payload?.organizationInfoName;
          if (!nome) {
            avisar(
              "erro",
              "Não dá para criar o SKU sem o código. Nada foi enviado.",
              "error"
            );
            break;
          }

          const newSKU: any = {
            organizationInfoType: 1,
            organizationInfoName: nome,
            organizationInfoDescription: payload.organizationInfoDescription,
            // O JSON inteiro é gravado como veio (o backend só filtra no
            // PATCH), então todo campo do formulário persiste na criação.
            organizationInfoValue: JSON.stringify(
              payload.organizationInfoValue ?? {}
            ),
            integrated: false,
            organizationInfoStatus: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            updatedByUuid: decryptedToken.userInfo.userUuid,
            organizationUuid:
              decryptedToken.userInfo.userGroupInfo[0].organization,
          };

          response = await api.post(`organizationInfo`, newSKU);

          setOrganizationZustand({
            InfoToken: response.data,
          });
          break;
        }
        case "updateSKU":
          const skuPayload = {
            ...payload,
            updatedAt: new Date().toISOString(),
            updatedByUuid: decryptedToken.userInfo.userUuid,
            organizationUuid:
              decryptedToken.userInfo.userGroupInfo[0].organization,
          };
          response = await api.patch(
            `organizationInfo/${payload.skuUUID}`,
            skuPayload
          );

          setOrganizationZustand({
            InfoToken: response.data,
          });
          break;
        // As duas strings que os modais REALMENTE mandam.
        //
        // `CompanyModal` chama handleSave("company") e `SubsidiaryModal` chama
        // handleSave("subsidiary"); o switch só conhecia "companyInfo" e
        // "subsidiaryInfo". As strings nunca bateram: os dois caíam no
        // `default: break`, nenhuma requisição saía e o modal fechava como se
        // tivesse salvado. Pior: os cases que existiam montavam a URL com
        // `selectedInfo.organizationInfoUuid`, e `selectedInfo` é o literal
        // `{ companyInfoType: 1 }` posto pelo clique na lista lateral — sem
        // uuid nenhum. Teria virado `PATCH organizationInfo/undefined`.
        //
        // O que se grava aqui são as COLUNAS da empresa/loja, contra as rotas
        // que já existem (`PATCH /company/:uuid`, `PATCH /subsidiary/:uuid`).
        // Os campos do formulário que NÃO têm coluna no schema estão listados
        // em CAMPOS_SEM_COLUNA e são recusados por nome, antes da requisição —
        // mandá-los faria o Prisma responder "Unknown argument" e derrubaria o
        // salvamento inteiro por causa de um campo que nunca existiu.
        case "company":
        case "subsidiary": {
          const ehEmpresa = objectType === "company";
          const uuid = ehEmpresa
            ? selectedObject?.companyUuid
            : selectedObject?.subsidiaryUuid;

          if (!uuid) {
            avisar(
              "erro",
              `Não dá para salvar: ${
                ehEmpresa ? "a empresa" : "a loja"
              } aberta não tem identificador. Feche e abra de novo pela lista.`,
              "error"
            );
            break;
          }

          const colunas: readonly string[] = ehEmpresa
            ? COLUNAS_DE_EMPRESA
            : COLUNAS_DE_LOJA;
          const semColuna: readonly string[] = ehEmpresa
            ? CAMPOS_SEM_COLUNA_EMPRESA
            : CAMPOS_SEM_COLUNA_LOJA;

          const corpo: any = {};
          for (const coluna of colunas) {
            if (formData?.[coluna] !== undefined) {
              corpo[coluna] = formData[coluna];
            }
          }
          const digitadosSemCasa = semColuna.filter(
            (campo) => formData?.[campo] !== undefined
          );

          if (Object.keys(corpo).length === 0) {
            avisar(
              "atencao",
              digitadosSemCasa.length > 0
                ? `Nada foi gravado: ${digitadosSemCasa.join(", ")} não ` +
                    `tem coluna no banco hoje (ver §18 da spec). Nenhum campo ` +
                    `com onde ser gravado foi alterado.`
                : "Nada mudou: nenhum campo foi alterado, então nada foi gravado.",
              "warning"
            );
            break;
          }

          corpo.updatedBy = decryptedToken.userInfo.userUuid;
          response = await api.patch(
            ehEmpresa ? `company/${uuid}` : `subsidiary/${uuid}`,
            corpo
          );

          avisar(
            digitadosSemCasa.length > 0 ? "atencao" : "ok",
            digitadosSemCasa.length > 0
              ? `Gravado: ${Object.keys(corpo)
                  .filter((c) => c !== "updatedBy")
                  .join(", ")}. NÃO gravado, por não ter coluna no banco: ` +
                  `${digitadosSemCasa.join(", ")}.`
              : `${ehEmpresa ? "Empresa" : "Loja"} salva.`,
            digitadosSemCasa.length > 0 ? "warning" : "success"
          );
          break;
        }

        // Ambiente. O modal monta o payload e NÃO faz requisição — quem grava é
        // este bloco, contra `POST /ambient` e `PATCH /ambient/:uuid`.
        //
        // `updatedBy` é montado AQUI, e não no modal, pelo mesmo motivo de
        // "company"/"subsidiary": o modal não tem o token do usuário. E ele não
        // é opcional — `createAmbientHandler` devolve 400 "updatedBy é
        // obrigatório" quando o corpo não o traz, porque este backend nunca
        // preenche `request.userUuid` (o fallback do handler é letra morta).
        case "ambient": {
          const uuid = payload?.ambientUuid ?? selectedObject?.ambientUuid;

          // Segunda trava, depois da do modal: o backend responde 400 sem
          // `ambientName`/`subsidiary`, e uma requisição que já se sabe perdida
          // não precisa sair.
          if (!payload?.ambientName?.trim() || !payload?.subsidiary) {
            avisar(
              "erro",
              "Nome do ambiente e loja são obrigatórios. Nada foi enviado.",
              "error"
            );
            break;
          }

          const corpo = {
            ambientName: payload?.ambientName,
            ambientDescription: payload?.ambientDescription,
            ambientType: payload?.ambientType,
            capacity: payload?.capacity,
            subsidiary: payload?.subsidiary,
            ambientStatus: payload?.ambientStatus,
            updatedBy: decryptedToken.userInfo.userUuid,
          };

          if (uuid) {
            await atualizarAmbiente(uuid, corpo);
          } else {
            await criarAmbiente(corpo as any);
          }

          // A lista de Ambientes é lida da travessia do InfoToken, que só é
          // reemitido no login — igual a "company"/"subsidiary". Então o
          // cadastro grava de verdade e a tela ainda mostra a lista antiga. Em
          // vez de deixar o usuário concluir que não salvou, o aviso diz isso.
          avisar(
            "ok",
            uuid
              ? "Ambiente salvo. A lista desta tela vem do token da sessão e só " +
                  "se atualiza no próximo login."
              : "Ambiente criado. A lista desta tela vem do token da sessão e só " +
                  "se atualiza no próximo login.",
            "success"
          );
          break;
        }

        // O modal de Períodos (7 seções, o mais completo do repo) chama
        // handleSave("periodType", localFormData) — e ia parar no bloco de
        // baixo, que faz `PATCH organizationInfo/${selectedInfo.organizationInfoUuid}`.
        // Período NÃO é organizationInfo: é `ActPeriodTypeInfo`, tabela
        // própria. E `selectedInfo` ali é `{ periodTypeInfoType: N }`, o
        // literal do clique na lista lateral — a URL sairia
        // `organizationInfo/undefined`.
        //
        // Não existe rota de periodTypeInfo neste backend (conferido em
        // common/all.routes.ts: são 10 routers, nenhum de período). Então a
        // recusa é NOMEADA e acontece antes de qualquer requisição, com o modal
        // aberto — em vez de fechar fingindo que gravou.
        case "periodType": {
          avisar(
            "erro",
            "Os períodos ainda não têm rota de gravação no servidor " +
              "(acd.\"ActPeriodTypeInfo\" não é exposta por nenhum router). " +
              "Nada foi enviado — a tela abre para leitura e conferência.",
            "error"
          );
          break;
        }

        case "companyInfo":
        case "subsidiaryInfo":
          const updatedInfo = {
            ...selectedInfo,
            updatedBy: decryptedToken.userInfo.userUuid,
            updatedAt: new Date().toISOString(),
            organizationInfoValue: JSON.stringify(tempFormData),
            organizationInfoType: selectedInfo.organizationInfoType,
            organizationInfoName: selectedInfo.organizationInfoName,
            organizationInfoDescription:
              selectedInfo.organizationInfoDescription,
            integrated: selectedInfo.integrated,
            organizationInfoStatus: selectedInfo.organizationInfoStatus,
            createdAt: selectedInfo.createdAt,
            organization: selectedInfo.organization,
          };

          response = await api.patch(
            `organizationInfo/${updatedInfo.organizationInfoUuid}`,
            updatedInfo
          );

          setOrganizationZustand({
            InfoToken: response.data,
          });
          break;

        // O botão "Salvar" do editor de PDV (ItemModal) sempre chamou
        // handleSave("item", ...), e este switch não tinha o case "item":
        // caía no default, nenhuma requisição saía e o modal fechava como se
        // tivesse salvado. Era por isso que createdAt == updatedAt em 100% das
        // linhas de itemInfo no banco — o PATCH nunca rodou.
        //
        // Agora salva de verdade, uma linha por vez, com a velocidade que o
        // usuário escolheu no rodapé. Cada Sale Option vai com o SEU próprio
        // valor (não com o objeto da tela inicial), e nenhuma linha é criada
        // ou apagada aqui.
        //
        // O que veio depois disso, e é o assunto deste bloco hoje: o save
        // gravava, mas MENTIA. Dizia "o PDV recebe em instantes" antes de saber
        // se o worker do OPS tinha aceitado, fechava o modal igual no erro e no
        // sucesso, e reescrevia a linha mesmo sem nenhuma edição. As recusas
        // (formato antigo, JSON quebrado, nada mudou) chegam prontas do modal —
        // aqui não sai requisição nenhuma para elas.
        case "item": {
          const aplicarEm = payload?.aplicarEm === "proximoPeriodo"
            ? "proximoPeriodo"
            : "agora";
          const itemUuid = selectedObject?.itemUuid ?? payload?.itemUuid;
          const telas = payload?.telas ?? {};
          const bloqueadas: string[] = telas.bloqueadasFormatoAntigo ?? [];
          const invalidas: string[] = telas.invalidas ?? [];
          const salvas: any[] = [];

          // Item novo: "Cadastrar Item" agora cria de verdade.
          //
          // Ficou desligado por duas causas, as duas consertadas no backend:
          // `POST /item` estava fora do `routeMappings` do hasPermission e
          // respondia 500 para todo mundo; e, atrás disso, `createItemHandler`
          // montava `updatedByUuid` a partir de `(request as any).userUuid`,
          // que este backend nunca popula — o Prisma recusava o create com
          // "Argument updatedByUuid is missing". Hoje o handler lê o usuário do
          // próprio token e exige itemName, itemType e ambientUuid.
          //
          // O ambiente NÃO se inventa. Item pendura em ambiente, e ambiente em
          // filial: chutar um ambiente "padrão" cadastraria o item na loja
          // errada, que é pior do que não cadastrar.
          //
          // Hoje um único ponto da tela sabe qual é o ambiente: o "Adicionar
          // Item" do estado vazio de um ambiente (`Items.tsx` passa
          // `{ ambient }`) — e ele só aparece quando o ambiente NÃO TEM nenhum
          // item. Os dois "Novo Item" do topo não sabem. Ou seja: o cadastro
          // liga de verdade, mas o caminho até ele é estreito, e alargá-lo
          // significa pôr um seletor de ambiente no modal — mudança visível no
          // modal de cadastro, que é justamente o que o contrato do gabarito
          // de 1440 não deixa fazer de passagem. Fica declarado, não implícito.
          //
          // Sem ambiente, a recusa diz o que fazer em vez de mandar uuid errado.
          if (!itemUuid) {
            const ambientUuid =
              selectedObject?.ambient?.ambientUuid ??
              selectedObject?.ambientUuid;

            if (!ambientUuid) {
              avisar(
                "atencao",
                "Escolha o ambiente antes de cadastrar: abra o ambiente na " +
                  'lista de Itens e use o "Adicionar Item" de dentro dele. O ' +
                  'botão "Novo Item" do topo não sabe em qual ambiente o item ' +
                  "entraria, e cadastrar no ambiente errado é pior do que não " +
                  "cadastrar. Nada foi criado.",
                "warning"
              );
              break;
            }

            if (!payload?.itemName || !payload?.itemType) {
              avisar(
                "erro",
                "Para cadastrar o item faltam nome e tipo. Nada foi criado.",
                "error"
              );
              break;
            }

            const criado = await itemsService.createItem({
              itemName: payload.itemName,
              itemType: payload.itemType,
              itemDescription: payload.itemDescription,
              ambientUuid,
            });

            // Sem o token novo, a árvore de Itens não mostra o item recém
            // criado: ela é lida do InfoToken do store, não de HTTP.
            if (criado?.infoToken) {
              setOrganizationZustand({ InfoToken: criado.infoToken });
            }

            avisar(
              "ok",
              `Item "${criado?.itemName ?? payload.itemName}" cadastrado. ` +
                "Abra o item para configurar as telas do PDV.",
              "success"
            );
            break;
          }

          // Formato antigo do Central (`components`/`styles`, sem
          // `saleOptionComponents`): o editor abriu a tela VAZIA e salvar dali
          // gravaria o vazio por cima do cardápio. É a mesma recusa que o
          // worker do OPS faria depois — antecipada para antes de sujar o banco.
          if (bloqueadas.length > 0) {
            avisar(
              "erro",
              `Esta tela está no formato antigo do Central. Rode o alinhamento ` +
                `OPS → Central antes de editar. (${bloqueadas.join(", ")}) ` +
                `Nada foi enviado.`,
              "error"
            );
            break;
          }

          // Nome, descrição e tipo do PRÓPRIO item (ActItem) — não passam pela
          // fila do PDV, porque não são configuração de tela.
          //
          // Este PATCH é o que faltava: o modal só mandava `itemInfo`, então
          // mudar o nome nunca gravou nada. Vai ANTES do laço de propósito —
          // assim, quando as telas também mudam, o `infoToken` que volta do
          // último `itemInfo` já traz o nome novo dentro, e é ele que fica
          // valendo no store.
          //
          // Só o que mudou de verdade é enviado: mandar o valor igual gravaria
          // updatedBy/updatedAt sem edição nenhuma, que é a mesma mentira que
          // este bloco tirou do salvamento de telas.
          const mudancasDoItem: {
            itemName?: string;
            itemDescription?: string;
            itemType?: number;
          } = {};
          if (
            payload?.itemName !== undefined &&
            payload.itemName !== selectedObject?.itemName
          ) {
            mudancasDoItem.itemName = payload.itemName;
          }
          if (
            payload?.itemDescription !== undefined &&
            payload.itemDescription !== (selectedObject?.itemDescription ?? "")
          ) {
            mudancasDoItem.itemDescription = payload.itemDescription;
          }
          if (
            payload?.itemType !== undefined &&
            payload.itemType !== selectedObject?.itemType
          ) {
            mudancasDoItem.itemType = payload.itemType;
          }

          let itemSalvo = false;
          if (Object.keys(mudancasDoItem).length > 0) {
            const atualizado = await itemsService.updateItem(
              itemUuid,
              mudancasDoItem
            );
            itemSalvo = true;
            // É ISTO que faz "mudar o nome, salvar, reabrir e ver o nome novo"
            // funcionar: a árvore Empresa → Loja → Ambiente → Itens vem do
            // InfoToken decodificado do store, não de uma chamada HTTP.
            if (atualizado?.infoToken) {
              setOrganizationZustand({ InfoToken: atualizado.infoToken });
            }
          }

          for (const saleOption of payload?.saleOptions || []) {
            if (!saleOption?.itemInfoUuid || !saleOption?.itemInfoValue) {
              continue;
            }
            const resposta = await api.patch(
              `itemInfo/${saleOption.itemInfoUuid}`,
              {
                itemInfoValue: saleOption.itemInfoValue,
                itemInfoType: saleOption.itemInfoType ?? 2,
                itemInfoName: saleOption.itemInfoName,
                itemInfoDescription: saleOption.itemInfoDescription,
                item: itemUuid,
                updatedBy: decryptedToken.userInfo.userUuid,
                aplicarEm,
              }
            );
            salvas.push(resposta.data);
          }

          const ultima = salvas[salvas.length - 1];
          if (ultima?.infoToken) {
            setOrganizationZustand({ InfoToken: ultima.infoToken });
          }

          const avisoDeInvalidas =
            invalidas.length > 0
              ? ` ${invalidas.length} tela(s) foram puladas porque o valor gravado ` +
                `não é um JSON válido (${invalidas.join(", ")}) — gravar por cima ` +
                `trocaria o valor por "{}".`
              : "";

          if (salvas.length === 0) {
            // O item pode ter sido gravado mesmo sem nenhuma tela mudar — é o
            // caso mais comum agora: renomear o item e salvar. Dizer "nada
            // mudou" aqui seria mentir sobre uma escrita que aconteceu.
            if (itemSalvo) {
              avisar(
                "ok",
                "Item atualizado. Nenhuma tela de PDV mudou, então nada foi " +
                  "enviado ao PDV." + avisoDeInvalidas,
                "success"
              );
              break;
            }

            const nenhumaTela = (telas.total ?? 0) === 0;
            avisar(
              "atencao",
              (nenhumaTela
                ? "Nada para salvar: este item não tem tela de PDV configurada."
                : "Nada mudou: nenhuma tela foi alterada, então nada foi gravado " +
                  "e nada foi enviado ao PDV.") + avisoDeInvalidas,
              "warning"
            );
            break;
          }

          // A mensagem não pode sair da contagem de requisições: um PATCH 2xx
          // só quer dizer que o evento entrou na fila. Quem decide se o PDV
          // aceita é o worker do OPS, e ele recusa por versão, por forma e por
          // encolhimento. Só a fila sabe o que aconteceu de verdade.
          //
          // O filtro é pelos eventoUuid DESTE salvamento: mostrar um conflito
          // antigo como se fosse resposta a este clique seria outra mentira.
          const meusEventos = new Set(
            salvas.map((s: any) => s?.fila?.eventoUuid).filter(Boolean)
          );
          const situacao = itemUuid
            ? await itemsService.getFilaPorItem(itemUuid).catch(() => null)
            : null;
          const meusConflitos = (situacao?.conflitos ?? []).filter((c: any) =>
            meusEventos.has(c.eventoUuid)
          );

          if (meusConflitos.length > 0) {
            avisar(
              "erro",
              `O PDV recusou ${meusConflitos.length} de ${salvas.length} tela(s): ` +
                `${meusConflitos[0].erro}` +
                avisoDeInvalidas,
              "error"
            );
            break;
          }

          avisar(
            "ok",
            (itemSalvo ? "Item atualizado. " : "") +
              (aplicarEm === "agora"
                ? `${salvas.length} tela(s) gravada(s) e na fila do PDV. Reabra o ` +
                  `item para ver se o PDV aceitou.`
                : `${salvas.length} tela(s) gravada(s). Entram em vigor na abertura ` +
                  `do próximo caixa.`) + avisoDeInvalidas,
            "success"
          );
          break;
        }

        // (O `case "ambient"` está mais acima, junto de "company"/"subsidiary".
        // Havia DOIS neste switch, escritos em paralelo: o segundo `case` de um
        // mesmo valor é código morto — o JS entra no primeiro e nunca chega no
        // outro. Ficou o que passa pelo service tipado.)

        // SUPRIMENTOS — os sete agregados que passaram a ter tabela, rota e
        // formulário. Todos seguem o mesmo caminho, contra o REST paginado.
        //
        // Nada daqui desce para o PDV, e a tela não promete que desce: a fila
        // `acfg."FilaConfig"` transporta hoje `itemInfo` e `subsidiaryInfo`, e
        // o worker do OPS recusa o que não conhece — agregado novo não vira
        // erro, vira evento PENDENTE PARA SEMPRE.
        case "createSupplier":
        case "updateSupplier":
        case "createLimit":
        case "updateLimit":
        case "createMovementType":
        case "updateMovementType":
        case "createBOM":
        case "updateBOM":
        case "createCategory":
        case "updateCategory":
        case "createGroup":
        case "updateGroup":
        case "createLocalEstoque":
        case "updateLocalEstoque": {
          const destino = AGREGADO_DO_MODAL[objectType as string];
          const uuid = selectedObject?.[destino.chave];
          const { precosAcordados, componentes, ...corpo } = payload ?? {};

          if (!corpo.nome?.trim() && destino.exigeNome) {
            avisar("erro", "O nome é obrigatório. Nada foi enviado.", "error");
            break;
          }

          const comAutor = {
            ...corpo,
            organizacao: corpo.organizacao ?? organizacaoDoUsuario,
            updatedBy: decryptedToken.userInfo.userUuid,
          };

          response = uuid
            ? await api.patch(`suprimentos/${destino.rota}/${uuid}`, comAutor)
            : await api.post(`suprimentos/${destino.rota}`, comAutor);

          const gravadoUuid = response.data?.dado?.[destino.chave] ?? uuid;

          // A lista de preços acordados viaja DENTRO do payload do fornecedor,
          // mas é outra tabela: cada linha vai numa requisição própria. A
          // sobreposição de vigência é recusada pelo servidor com 409 — é lá
          // que a trava mora, não aqui.
          let precosGravados = 0;
          let precosRecusados: string[] = [];
          if (Array.isArray(precosAcordados) && gravadoUuid) {
            for (const preco of precosAcordados) {
              try {
                const linha = {
                  ...preco,
                  fornecedor: gravadoUuid,
                  updatedBy: decryptedToken.userInfo.userUuid,
                };
                if (preco.precoAcordadoUuid) {
                  await api.patch(
                    `suprimentos/precos-acordados/${preco.precoAcordadoUuid}`,
                    linha
                  );
                } else {
                  await api.post(`suprimentos/precos-acordados`, linha);
                }
                precosGravados += 1;
              } catch (erroDoPreco: any) {
                precosRecusados.push(
                  erroDoPreco?.response?.data?.message ?? "recusado"
                );
              }
            }
          }

          if (precosRecusados.length > 0) {
            avisar(
              "atencao",
              `${destino.rotulo} salvo, mas ${precosRecusados.length} preço(s) ` +
                `acordado(s) foram recusados: ${precosRecusados[0]}`,
              "warning"
            );
            break;
          }

          avisar(
            "ok",
            `${destino.rotulo} ${uuid ? "atualizado" : "criado"}.` +
              (precosGravados > 0
                ? ` ${precosGravados} preço(s) acordado(s) gravado(s).`
                : ""),
            "success"
          );
          break;
        }

        // O QUE AINDA NÃO TEM ROTA — recusa NOMEADA, antes de qualquer
        // requisição, com o modal aberto.
        //
        // Estes formulários existem e estão completos; o que falta é o
        // endpoint. Dizer QUAL rota falta é o que separa "ainda não" de "quebrou
        // e ninguém sabe". A alternativa que o card proíbe explicitamente é
        // fingir persistência com localStorage ou inflar o InfoToken.
        case "alertConfig":
        case "messageTemplate":
        case "canalComunicacao":
        case "automacao":
        case "desconto":
        case "taxa":
        case "employee":
        case "access": {
          avisar("erro", RECUSA_SEM_ROTA[objectType as string], "error");
          break;
        }

        // O modo de falha padrão deste arquivo era ESTE `break`: qualquer
        // objectType sem case fechava o modal como se tivesse salvado. Caíam
        // aqui, em silêncio: employee, access, schedule, timeSheet (o
        // employeesSection inteiro), personalData, preferences, security,
        // feedback, notificationSettings, permission, log, integration.
        //
        // Agora a recusa tem nome e o modal fica aberto. "Ainda não está
        // ligado" é uma informação; um modal que some não é.
        default:
          avisar(
            "erro",
            `"${objectType}" ainda não tem gravação ligada ao servidor. ` +
              "Nada foi enviado.",
            "error"
          );
          break;
      }
    } catch (error: any) {
      const detalhe =
        error?.response?.data?.message ?? error?.message ?? String(error);
      avisar("erro", `Erro ao salvar informações: ${detalhe}`, "error");
    }

    updateState({ isEditing: false });

    if (!manterModalAberto) {
      handleCloseModal();
      updateState({ selectedInfo: null });
    }

    return resultado;
  };

  const renderSessionContent = () => {
    switch (selectedHorizontalOption) {
      case "Minha Conta":
        return (
          <AccountSection
            selectedVerticalOption={selectedVerticalOption}
            isEditing={isEditing}
            formData={formData}
            decryptedToken={decryptedToken}
            handleInputChange={handleInputChange}
            handleSave={handleSave}
            updateState={updateState}
            uploadedImage={uploadedImage}
          />
        );
      case "Lojas":
        return (
          <StoresSection
            selectedVerticalOption={selectedVerticalOption}
            isEditing={isEditing}
            formData={formData}
            decryptedInfoToken={decryptedInfoToken}
            handleInputChange={handleInputChange}
            handleSave={handleSave}
            updateState={updateState}
            handleOpenModal={handleOpenModal}
            expandedAccordion={expandedAccordion}
            toggleAccordion={toggleAccordion}
          />
        );
      case "Suprimentos":
        return (
          <SuppliesSection
            selectedVerticalOption={selectedVerticalOption}
            isEditing={isEditing}
            formData={formData}
            decryptedInfoToken={decryptedInfoToken}
            handleInputChange={handleInputChange}
            handleSave={handleSave}
            updateState={updateState}
            handleOpenModal={handleOpenModal}
          />
        );
      // As três abas novas. Cada uma delas exigiu as MESMAS TRÊS edições
      // casadas por string, sem type-check ligando as três: `horizontalOptions`,
      // a chave de `verticalOptions` e este `case`. Divergir dá tela em branco
      // silenciosa — foi assim que "Lista de Materiais" ficou construída e
      // inalcançável por meses.
      case "Cardápio":
        return (
          <CardapioSection
            selectedVerticalOption={selectedVerticalOption}
            decryptedInfoToken={decryptedInfoToken}
          />
        );
      case "Descontos e Taxas":
        return (
          <DescontosSection
            selectedVerticalOption={selectedVerticalOption}
            decryptedInfoToken={decryptedInfoToken}
            handleSave={handleSave}
            handleOpenModal={handleOpenModal}
            updateState={updateState}
          />
        );
      case "Comunicações":
        return (
          <ComunicacoesSection
            selectedVerticalOption={selectedVerticalOption}
            decryptedInfoToken={decryptedInfoToken}
            handleSave={handleSave}
            handleOpenModal={handleOpenModal}
          />
        );
      case "Colaboradores":
        return (
          <EmployeesSection
            selectedVerticalOption={selectedVerticalOption}
            isEditing={isEditing}
            formData={formData}
            decryptedInfoToken={decryptedInfoToken}
            handleInputChange={handleInputChange}
            handleSave={handleSave}
            updateState={updateState}
          />
        );
      case "Avançado":
        return (
          <AdvancedSection
            selectedVerticalOption={selectedVerticalOption}
            isEditing={isEditing}
            formData={formData}
            decryptedInfoToken={decryptedInfoToken}
            handleInputChange={handleInputChange}
            handleSave={handleSave}
            updateState={updateState}
          />
        );
      default:
        return (
          <div>{`Você selecionou: ${selectedHorizontalOption} > ${selectedVerticalOption}`}</div>
        );
    }
  };

  const renderModalContent = () => {
    if (!isModalOpen) return null;

    switch (modalType) {
      case "company":
        return (
          <CompanyModal
            selectedObject={selectedObject}
            selectedInfo={selectedInfo}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            handleInputChange={handleInputChange}
            setSelectedInfo={(info: any) => updateState({ selectedInfo: info })}
            setTempFormData={(data: any) => updateState({ tempFormData: data })}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
            handleOpenModal={handleOpenModal}
          />
        );
      case "subsidiary":
        return (
          <SubsidiaryModal
            selectedObject={selectedObject}
            selectedInfo={selectedInfo}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            handleInputChange={handleInputChange}
            setSelectedInfo={(info: any) => updateState({ selectedInfo: info })}
            setTempFormData={(data: any) => updateState({ tempFormData: data })}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
            handleOpenModal={handleOpenModal}
          />
        );
      case "periodType":
        return (
          <PeriodTypeModal
            selectedObject={selectedObject}
            selectedInfo={selectedInfo}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            handleInputChange={handleInputChange}
            setSelectedInfo={(info: any) => updateState({ selectedInfo: info })}
            setTempFormData={(data: any) => updateState({ tempFormData: data })}
            parseInfoValue={parseInfoValue}
            filter={filter}
            updateState={updateState}
          />
        );
      case "item":
        return (
          <ItemModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            setTempFormData={(data: any) => updateState({ tempFormData: data })}
            tempFormData={tempFormData}
          />
        );
      // `handleOpenModal("ambient", ...)` aparece em 8 pontos (6 em
      // Ambients.tsx, 2 em Subsidiary.tsx) e este case não existia: o clique
      // marcava `modalType = "ambient"` e caía no `default: return null`. O
      // modal abria vazio — quer dizer, não abria nada, e o usuário ficava
      // olhando para um botão que não faz efeito.
      case "ambient":
        return (
          <AmbientModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            decryptedInfoToken={decryptedInfoToken}
          />
        );
      case "createSKU":
        return (
          <CreateSKUModal
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            setRef={setRef}
          />
        );
      case "updateSKU":
        return (
          <UpdateSKUModal
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            selectedObject={selectedObject}
          />
        );

      // OS NOVE MODAIS FANTASMA.
      //
      // Todos estes `modalType` já eram disparados pelas telas e caíam no
      // `default: return null` abaixo: o usuário clicava em "Novo fornecedor",
      // "Editar limite", "Configurar alerta" ou "Novo ambiente" e NADA abria.
      // Sem erro, sem aviso, sem nada.
      //
      // O mesmo componente atende criar e editar: quem decide é o uuid dentro
      // de `selectedObject`, nunca a existência dele (o useModal faz
      // `object || {}`, e `{}` é truthy).
      //
      // (O `case "ambient"` deste switch está mais acima, logo depois de
      // "item". Havia dois, e o segundo era inalcançável.)
      case "createSupplier":
      case "updateSupplier":
        return (
          <SupplierModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
          />
        );
      case "createLimit":
      case "updateLimit":
        return (
          <LimitModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
          />
        );
      case "createMovementType":
      case "updateMovementType":
        return (
          <MovementTypeModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
          />
        );
      case "createBOM":
      case "updateBOM":
        return (
          <BOMModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
          />
        );
      case "createCategory":
      case "updateCategory":
        return (
          <CategoryModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
          />
        );
      case "createGroup":
      case "updateGroup":
        return (
          <GroupModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
          />
        );
      case "createLocalEstoque":
      case "updateLocalEstoque":
        return (
          <LocalDeEstoqueModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
          />
        );
      case "alertConfig":
        return (
          <AlertConfigModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
          />
        );
      case "messageTemplate":
        return (
          <MessageTemplateModal
            selectedObject={selectedObject}
            handleCloseModal={handleCloseModal}
            handleSave={handleSave}
            parseInfoValue={parseInfoValue}
            decryptedInfoToken={decryptedInfoToken}
          />
        );

      default:
        return null;
    }
  };

  /*
   * As duas faixas de aba passam a existir para o teclado. Ver o cabeçalho de
   * `useAbasAcessiveis`: medido, a faixa inteira tinha ZERO elementos focáveis,
   * o que deixava a Configuração — loja, cardápio, SKU, impressora — acessível
   * só com mouse.
   */
  const { propsDaFaixa: propsDaFaixaDeCima, propsDaAba: propsDaAbaDeCima } =
    useAbasAcessiveis({
      itens: horizontalOptions,
      selecionada: selectedHorizontalOption,
      aoSelecionar: (aba) => irParaAba(aba, verticalOptions[aba][0]),
      orientacao: "horizontal",
      rotulo: "Seções da configuração",
    });

  const opcoesDeLado = verticalOptions[selectedHorizontalOption] ?? [];
  const { propsDaFaixa: propsDaFaixaDeLado, propsDaAba: propsDaAbaDeLado } =
    useAbasAcessiveis({
      itens: opcoesDeLado,
      selecionada: selectedVerticalOption,
      aoSelecionar: (aba) => irParaAba(selectedHorizontalOption, aba),
      orientacao: "vertical",
      rotulo: `Telas de ${selectedHorizontalOption}`,
    });

  return (
    <AlfredoStyles.Card>
      <AlfredoStyles.Icon>settings</AlfredoStyles.Icon>
      <AlfredoStyles.Title>Configure seu Alfredo</AlfredoStyles.Title>
      <AlfredoStyles.HorizontalOptions {...propsDaFaixaDeCima}>
        {horizontalOptions.map((option) => (
          <AlfredoStyles.HorizontalOptionItem
            key={option}
            isSelected={selectedHorizontalOption === option}
            {...propsDaAbaDeCima(option)}
            onClick={() => irParaAba(option, verticalOptions[option][0])}
          >
            {option}
          </AlfredoStyles.HorizontalOptionItem>
        ))}
      </AlfredoStyles.HorizontalOptions>
      <AlfredoStyles.ConfigurationContent>
        <AlfredoStyles.VerticalOptions {...propsDaFaixaDeLado}>
          {(verticalOptions[selectedHorizontalOption] ?? []).map((option) => (
            <AlfredoStyles.VerticalOptionItem
              key={option}
              isSelected={selectedVerticalOption === option}
              {...propsDaAbaDeLado(option)}
              onClick={() => irParaAba(selectedHorizontalOption, option)}
            >
              {option}
            </AlfredoStyles.VerticalOptionItem>
          ))}
        </AlfredoStyles.VerticalOptions>
        <AlfredoStyles.SessionConfigurationContainer>
          {/* `key` força um boundary novo a cada sub-tela: sem isso, o cartão
              de erro de uma tela quebrada continuaria montado ao trocar de aba
              e contaminaria a tela seguinte, que está sã. */}
          <ErrorBoundary
            key={`${selectedHorizontalOption}/${selectedVerticalOption}`}
            ondeEstou={`${selectedHorizontalOption} › ${selectedVerticalOption}`}
          >
            {renderSessionContent()}
          </ErrorBoundary>
        </AlfredoStyles.SessionConfigurationContainer>
      </AlfredoStyles.ConfigurationContent>
      {renderModalContent()}
    </AlfredoStyles.Card>
  );
};

export default Configuration;
